import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import CheckIcon from 'assets/svg/circle-check.svg';
import { H2, DefaultText, SmallText } from 'ds/components/typography';
import { useLocale } from 'locale';
import { EnhancedPromotionPlanInfo } from 'components/private-bulletin-promotion/modals/private-bulletin-select-promotion-plan-modal/types';
import { Button } from 'ds/components/button';
import { useCountdown, formatCountdown } from 'hooks/useCountdown';

export type PricingCardVariant = 'primary' | 'secondary';

interface PricingCardProps {
  variant: PricingCardVariant;
  isLoading?: boolean;
  plan: EnhancedPromotionPlanInfo;
  onSelect: () => void;
}

const CardContainer = styled.div<{ variant: PricingCardVariant }>(({ theme, variant }) => ({
  position: 'relative',
  minWidth: '316px',
  height: '412px',
  backgroundColor: theme.colors.neutrals.white,
  borderRadius: theme.borderRadius.large,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: theme.spacing(8, 2, 2, 2),
  boxSizing: 'border-box',

  borderWidth: variant === 'primary' ? '2px' : '1px',
  borderStyle: 'solid',
  borderColor: variant === 'primary'
    ? theme.colors.brand.madlanGreen
    : theme.colors.neutrals.grey6,
}));

const Badge = styled(SmallText)<{ variant: PricingCardVariant }>(({ theme, variant }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  padding: theme.spacing(0.5, 3),
  backgroundColor: variant === 'primary'
    ? theme.colors.brand.madlanGreen
    : theme.colors.brand.main,
  borderBottomRightRadius: theme.borderRadius.large,
  color: theme.colors.neutrals.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const CountdownTime = styled.span({
  display: 'inline-block',
  width: '52px',
  textAlign: 'left',
});

const ContentArea = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  [theme.media.createObjectMediaQuery({ from: 3 })]: {
    maxWidth: '284px',
  },
}));

const HeaderTitle = styled(H2)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
}));

const Subtitle = styled(SmallText)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
  marginTop: theme.spacing(0.25),
}));

const FeatureList = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const FeatureRow = styled.div<{ disabled: boolean; }>(({ theme, disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(1.5),
  ...(disabled ? {
    ' > div': {
      color: theme.colors.neutrals.grey6,
    },
    'svg': {
      filter: 'grayscale(100%)',
      path: {
        display: 'none',
      },
    },
  } : {}),
}));

const CheckIconWrapper = styled.div(({ theme }) => ({
  width: '20px',
  height: '20px',
  minWidth: '20px',
  borderRadius: theme.borderRadius.round,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const FeatureText = styled(SmallText)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
}));

const Description = styled(SmallText)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
  whiteSpace: 'pre-line',
}));

const Footer = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const PriceRow = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.spacing(0.5),
}));

const PriceAmount = styled(DefaultText)(({ theme }) => ({
  fontSize: '32px',
  color: theme.colors.neutrals.grey1,
  lineHeight: 1,
}));

const FullPriceText = styled(SmallText)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
  textDecoration: 'line-through',
}));

const PriceSubtext = styled(SmallText)(({ theme }) => ({
  color: theme.colors.neutrals.grey2,
}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.colors.brand.madlanGreen,
  color: theme.colors.neutrals.white,
  border: 'none',
  '&:hover': {
    backgroundColor: '#13ae6f',
    borderColor: '#13ae6f',
    opacity: 0.9,
  },
}));

const CORNER_LABEL_COUNTDOWN_PREFIX = 'זמן לניצול ההטבה - ';
const NO_COUNTDOWN_DATE = new Date(0);

export const PricingCard: React.FC<PricingCardProps> = (props) => {
  const {
    variant = 'primary',
    onSelect,
    plan,
    isLoading,
  } = props;
  const { t, formatMoney } = useLocale();
  const { cardCornerLabel, offerValidUntil } = plan;
  const showCountdown = Boolean(cardCornerLabel && cardCornerLabel.showCountdown && offerValidUntil);

  const countdownEndDate = useMemo(() => (
    offerValidUntil ? new Date(offerValidUntil) : NO_COUNTDOWN_DATE
  ), [ offerValidUntil ]);
  const timeLeft = useCountdown(countdownEndDate);

  let priceSubtextElement: React.ReactNode = null;
  if (plan.fullPrice) {
    priceSubtextElement = <FullPriceText>{formatMoney(plan.fullPrice)}</FullPriceText>;
  }
  else if (plan.priceSubtext) {
    priceSubtextElement = <PriceSubtext>{plan.priceSubtext}</PriceSubtext>;
  }

  const cornerLabelText = showCountdown
    ? (
      <>
        {CORNER_LABEL_COUNTDOWN_PREFIX}
        <CountdownTime>{formatCountdown(timeLeft)}</CountdownTime>
      </>
    )
    : (cardCornerLabel ? cardCornerLabel.text : null);

  return (
    <CardContainer variant={variant}>
      {cornerLabelText ? (
        <Badge weight="bold" variant={variant}>
          {cornerLabelText}
        </Badge>
      ) : null}

      <ContentArea>
        <div>
          <HeaderTitle weight="bold">{plan.planTitle}</HeaderTitle>
          {plan.planSubtitle ? <Subtitle>{plan.planSubtitle}</Subtitle> : null}
        </div>

        {plan.cardDescription ? (
          <Description>{plan.cardDescription}</Description>
        ) : (
          <FeatureList>
            {plan.features.map(feature => {
              return (
                <FeatureRow
                  key={feature.id}
                  disabled={feature.disabled}
                >
                  <CheckIconWrapper><CheckIcon /></CheckIconWrapper>
                  <FeatureText>
                    {feature.text}
                  </FeatureText>
                </FeatureRow>
              );
            })}
          </FeatureList>
        )}
      </ContentArea>

      <Footer>
        <PriceRow>
          <PriceAmount weight="bold">{formatMoney(plan.price)}</PriceAmount>
          {priceSubtextElement}
        </PriceRow>
          {variant === 'primary' ? (
            <ActionButton
              fullWidth
              size="large"
              weight="medium"
              disabled={isLoading}
              onClick={onSelect}
              data-auto={`bulletin_form_wizard_plan_${plan.type}`}
            >
              {t('pricingCard.cta')}
            </ActionButton>
          ) : (
            <Button
              fullWidth
              size="large"
              weight="medium"
              mode="ghost-black-text"
              onClick={onSelect}
              disabled={isLoading}
              data-auto={`bulletin_form_wizard_plan_${plan.type}`}
            >
              {plan.submitButtonText || t('pricingCard.cta')}
            </Button>
          )}
      </Footer>
    </CardContainer>
  );
};
